"use client"

/**
 * Live preview surface for /tools/aeration-calculator. Status banner +
 * best-window date + 12-month calendar + "why now" panel. Updates on
 * every input change in the form, no submit gate.
 */

import {
  type AerationPlan,
  fmtRange,
  MONTH_FULL,
} from "@/lib/aeration-plan"
import {
  YearCalendar,
  type YearCalendarCell,
} from "@/components/tools/shared/year-calendar"

const A_INK = "#111827"
const A_INK2 = "#374151"
const A_RULE = "#e5e7eb"
const A_BRAND = "#16a34a"
const A_BRAND_700 = "#15803d"
const A_BRAND_50 = "#f0fdf4"
const A_BRAND_100 = "#dcfce7"
const A_AMBER = "#f59e0b"
const A_AMBER_50 = "#fffbeb"
const A_BG_ALT = "#f9fafb"
const A_MUTED = "#6b7280"

const STATUS_LABEL: Record<AerationPlan["status"], string> = {
  now: "Aerate now",
  soon: "Get ready",
  optional: "In window — optional",
  wait: "Wait",
}

function statusDetail(plan: AerationPlan): string {
  const range = fmtRange(plan.bestStart, plan.bestEnd)
  switch (plan.status) {
    case "now":
      return `You're in the window and overdue. Aim for ${range}.`
    case "soon":
      return `You're overdue, but next window opens ${MONTH_FULL[plan.bestIdx]}.`
    case "optional":
      return `You're in the window. Aerate now if compacted, otherwise next year.`
    case "wait":
      return `Next window is ${MONTH_FULL[plan.bestIdx]}. Don't aerate outside it.`
  }
}

export interface AerationPreviewProps {
  plan: AerationPlan
  /** Compact variant for the mobile above-fold card. */
  compact?: boolean
}

export function AerationPreview({ plan, compact = false }: AerationPreviewProps) {
  const today = new Date()
  const thisMonth = today.getMonth()

  const statusColor =
    plan.status === "now" ? A_BRAND : plan.status === "soon" ? A_AMBER : A_INK2
  const statusBg =
    plan.status === "now"
      ? A_BRAND_50
      : plan.status === "soon"
        ? A_AMBER_50
        : A_BG_ALT

  if (compact) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: `1px solid ${A_RULE}`,
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          padding: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: statusColor,
              background: statusBg,
              padding: "3px 8px",
              borderRadius: 9999,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
            }}
          >
            {STATUS_LABEL[plan.status]}
          </span>
          <span style={{ fontSize: 11, color: A_MUTED }}>· {plan.grass.tag}</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: A_INK,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            marginBottom: 4,
          }}
        >
          {fmtRange(plan.bestStart, plan.bestEnd)}
        </div>
        <div style={{ fontSize: 12, color: A_MUTED, marginBottom: 12 }}>
          Soil target: {plan.grass.optimalSoil}
        </div>
        <YearCalendar
          variant="compact"
          months={toAerationCells(plan)}
          thisMonth={thisMonth}
          bestMonth={plan.bestIdx}
          ringColors={{ today: A_INK2, best: A_BRAND_700 }}
          ariaLabel="12-month aeration calendar"
        />
      </div>
    )
  }

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${A_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        padding: "24px 24px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        color: A_INK,
      }}
    >
      {/* Status banner */}
      <div
        style={{
          background: statusBg,
          borderRadius: 10,
          padding: "14px 16px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            flexShrink: 0,
            background: "#fff",
            color: statusColor,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CalIcon size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: statusColor,
              marginBottom: 2,
            }}
          >
            {STATUS_LABEL[plan.status]}
          </div>
          <div style={{ fontSize: 13, color: A_INK2, lineHeight: 1.5 }}>
            {statusDetail(plan)}
          </div>
        </div>
      </div>

      {/* Best window */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: A_BRAND_700,
            marginBottom: 8,
          }}
        >
          Best window
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 30,
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.025em",
              color: A_INK,
            }}
          >
            {fmtRange(plan.bestStart, plan.bestEnd)}
          </span>
        </div>
        <div
          style={{
            fontSize: 13,
            color: A_MUTED,
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ThermIcon size={13} /> Soil temperature target: {plan.grass.optimalSoil}
        </div>
      </div>

      <div style={{ height: 1, background: A_RULE }} />

      {/* 12-month timeline */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: A_INK2,
            marginBottom: 12,
          }}
        >
          Year at a glance
        </div>
        <YearCalendar
          variant="full"
          months={toAerationCells(plan)}
          thisMonth={thisMonth}
          bestMonth={plan.bestIdx}
          ringColors={{ today: A_INK2, best: A_BRAND_700 }}
          todayDot
          ariaLabel="12-month aeration calendar"
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginTop: 12,
            fontSize: 11.5,
            color: A_MUTED,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: A_BRAND }} />
            Primary
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
              style={{ width: 12, height: 12, borderRadius: 2, background: A_BRAND_100 }}
            />
            Secondary
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: A_INK }} />
            Today
          </span>
        </div>
      </div>

      <div style={{ height: 1, background: A_RULE }} />

      {/* Why now */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: A_INK2,
            marginBottom: 10,
          }}
        >
          Why now
        </div>
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Reason>
            {plan.grass.season === "cool"
              ? "Cool-season grass aerates best in early fall — roots recover before winter."
              : "Warm-season grass aerates best in late spring as it enters peak growth."}
          </Reason>
          <Reason>
            Soil hits {plan.grass.optimalSoil} in {MONTH_FULL[plan.bestIdx]}, the recovery sweet spot.
          </Reason>
          <Reason>
            With <strong style={{ color: A_INK }}>{plan.traffic.name.toLowerCase()}</strong>{" "}
            traffic, plan to aerate every {plan.traffic.due} months.
          </Reason>
        </ul>
      </div>
    </div>
  )
}

function toAerationCells(plan: AerationPlan): YearCalendarCell[] {
  return plan.months.map(m => ({
    i: m.i,
    label: m.label,
    fill:
      m.kind === "primary"
        ? A_BRAND
        : m.kind === "secondary"
          ? A_BRAND_100
          : A_BG_ALT,
  }))
}

function Reason({ children }: { children: React.ReactNode }) {
  return (
    <li
      style={{
        display: "flex",
        gap: 10,
        fontSize: 13.5,
        color: A_INK2,
        lineHeight: 1.55,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          flexShrink: 0,
          marginTop: 1,
          borderRadius: 9,
          background: A_BRAND_100,
          color: A_BRAND_700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon size={11} />
      </span>
      <span>{children}</span>
    </li>
  )
}

function CalIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  )
}
function ThermIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 14V5a2 2 0 10-4 0v9a4 4 0 104 0z" />
      <circle cx="12" cy="17" r="1.5" fill="currentColor" />
    </svg>
  )
}
function CheckIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l4 4 10-10" />
    </svg>
  )
}
